import { AudioCapturePhase } from "@/store/audio/pipeline/audio-capture.types";
import type { AudioCaptureTranscriptState } from "../audio-capture-actions.types";

export enum AudioCaptureTranscriptViewMode {
  Transcript = "transcript",
  StatusOnly = "status-only",
  FailureOnly = "failure-only",
  None = "none",
}

export type AudioCaptureTranscriptViewState = {
  mode: AudioCaptureTranscriptViewMode;
  showListeningStatus: boolean;
  showTranscriptionUnsupported: boolean;
  showDisconnected: boolean;
  showCompromised: boolean;
  showFailureMessage: boolean;
  shouldShowAudioSilentWarning: boolean;
};

export const getAudioCaptureTranscriptViewState = ({
  transcript,
  phase,
  isAudioSilent,
  audioRecordingFailureMessage,
}: {
  transcript: AudioCaptureTranscriptState;
  phase: AudioCapturePhase;
  isAudioSilent: boolean;
  audioRecordingFailureMessage?: string | null;
}): AudioCaptureTranscriptViewState => {
  const showListeningStatus =
    phase === AudioCapturePhase.Initializing || phase === AudioCapturePhase.Recording;
  const shouldShowAudioSilentWarning = isAudioSilent && phase !== AudioCapturePhase.Paused;

  const hasTranscript = transcript.hasTranscript;
  const showTranscriptionUnsupported = !hasTranscript && transcript.isTranscriptionUnsupported;
  const showFailureMessage = Boolean(audioRecordingFailureMessage);
  const showCompromised = transcript.isCompromised;

  const showDisconnected = getShouldShowDisconnected({
    transcript,
    phase,
    hasTranscript,
  });

  const mode = getTranscriptViewMode({
    hasTranscript,
    showTranscriptionUnsupported,
    showListeningStatus,
    showFailureMessage,
  });

  return {
    mode,
    showListeningStatus,
    showTranscriptionUnsupported,
    showDisconnected,
    showCompromised,
    showFailureMessage,
    shouldShowAudioSilentWarning,
  };
};

const getShouldShowDisconnected = ({
  transcript,
  phase,
  hasTranscript,
}: {
  transcript: AudioCaptureTranscriptState;
  phase: AudioCapturePhase;
  hasTranscript: boolean;
}): boolean => {
  if (!transcript.isDisconnected || transcript.isCompromised) {
    return false;
  }

  if (hasTranscript) {
    return phase === AudioCapturePhase.Recording;
  }

  return true;
};

const getTranscriptViewMode = ({
  hasTranscript,
  showTranscriptionUnsupported,
  showListeningStatus,
  showFailureMessage,
}: {
  hasTranscript: boolean;
  showTranscriptionUnsupported: boolean;
  showListeningStatus: boolean;
  showFailureMessage: boolean;
}): AudioCaptureTranscriptViewMode => {
  if (hasTranscript) {
    return AudioCaptureTranscriptViewMode.Transcript;
  }

  if (showTranscriptionUnsupported || showListeningStatus) {
    return AudioCaptureTranscriptViewMode.StatusOnly;
  }

  if (showFailureMessage) {
    return AudioCaptureTranscriptViewMode.FailureOnly;
  }

  return AudioCaptureTranscriptViewMode.None;
};
